/**
 * Leads (vanilla Leashable). A leashed mob is held by the player, another mob
 * or a knot on a fence. DOM-free: the game glue (main.ts) feeds `LeashCtx` once
 * per game tick, after the mobs ticked, and calls the `leadUse*` helpers from
 * the right-click paths.
 *
 * Physics (vanilla): within 6 blocks of the holder the mob wanders on its own
 * (stroll targets are limited to 5 blocks around the holder through
 * `Mob.leashHome`); past 6 it is pulled elastically (velocity toward the
 * holder, sign(c) * c^2 * 0.4 per axis with c the unit direction); past 10
 * the lead snaps and drops. A dead or removed mob, a dead holder, a broken
 * fence or a dead player also release the lead. Knots are not entities: a knot
 * exists while some mob is held by it.
 */

import { SHAPE_STATE } from '../blocks';
import type { World } from '../world/chunk';
import type { Mob } from './mob';

/** `id` on a player holder: which player (a server with many); absent = the local player. */
export type LeashHolder = { kind: 'player'; id?: string } | { kind: 'mob'; id: number } | { kind: 'knot'; x: number; y: number; z: number };

/** Mob types that accept a lead (vanilla Leashable). Villagers, traders, hostiles, bats and fish do not. */
const LEASHABLE = new Set<string>([
  'cow', 'pig', 'sheep', 'chicken', 'horse', 'donkey', 'wolf', 'cat', 'parrot', 'panda', 'squid', 'strider', 'hoglin',
  'iron_golem', 'llama', 'trader_llama', 'fox', 'mule', 'camel', 'goat', 'bee', 'axolotl', 'ocelot', 'zoglin',
]);

/** Snap distance and the distance where the elastic pull starts (vanilla 10 / 6). */
export const LEASH_MAX = 10;
export const LEASH_PULL = 6;
/** Radius around the holder the mob strolls in while leashed. */
export const LEASH_WANDER = 5;

export function canLeash(mob: Mob): boolean { return LEASHABLE.has(mob.type) && mob.alive; }

/** What the sim needs from the game each tick. */
export interface LeashCtx {
  /** Local player position (feet) and liveness. */
  player: { x: number; y: number; z: number; alive: boolean };
  /** A player holder that carries an id (server). Absent or undefined result: that player is gone, the lead drops. */
  playerById?(id: string): { x: number; y: number; z: number; alive: boolean } | undefined;
  /** Look up a live mob (holder of another). */
  mobById(id: number): Mob | undefined;
  /** Drop one lead item at a position. */
  dropLead(x: number, y: number, z: number): void;
  /** Is the fence the knot hangs on still there? Absent: always. */
  knotValid?(x: number, y: number, z: number): boolean;
}

export const isFence = (id: number): boolean => SHAPE_STATE[id]?.kind === 'fence';

export class LeashSim {
  /** mob -> holder. Iteration order = attach order. */
  private readonly held = new Map<Mob, LeashHolder>();
  /** Last ctx (detach needs somewhere to drop the lead). */
  private last: LeashCtx | null = null;
  /** Bumped on every change (renderers / net can cache by it). */
  version = 0;

  attach(mob: Mob, holder: LeashHolder): boolean {
    if (!canLeash(mob) || this.held.has(mob)) return false;
    if (holder.kind === 'mob' && holder.id === mob.id) return false;
    this.held.set(mob, holder);
    this.version++;
    return true;
  }

  /** Release `mob`; `drop` = drop a lead item at the mob. */
  detach(mob: Mob, drop: boolean): void {
    if (!this.held.delete(mob)) return;
    this.version++;
    mob.leashHome = null;
    if (drop) this.last?.dropLead(mob.x, mob.y + 0.5, mob.z);
  }

  holderOf(mob: Mob): LeashHolder | null { return this.held.get(mob) ?? null; }
  isLeashed(mob: Mob): boolean { return this.held.has(mob); }

  /** Mobs currently held by the local player. */
  heldByPlayer(id?: string): Mob[] {
    const out: Mob[] = [];
    for (const [m, h] of this.held) if (h.kind === 'player' && (id === undefined || h.id === id)) out.push(m);
    return out;
  }

  /** Mobs tied to the knot at a fence cell. */
  heldByKnot(x: number, y: number, z: number): Mob[] {
    const out: Mob[] = [];
    for (const [m, h] of this.held) if (h.kind === 'knot' && h.x === x && h.y === y && h.z === z) out.push(m);
    return out;
  }

  /** Every leash: for rendering, saving and replication. */
  links(): Array<{ mob: Mob; holder: LeashHolder }> {
    const out: Array<{ mob: Mob; holder: LeashHolder }> = [];
    for (const [mob, holder] of this.held) out.push({ mob, holder });
    return out;
  }

  /** Distinct knot cells in use. */
  knots(): Array<{ x: number; y: number; z: number }> {
    const seen = new Set<string>();
    const out: Array<{ x: number; y: number; z: number }> = [];
    for (const h of this.held.values()) {
      if (h.kind !== 'knot') continue;
      const k = h.x + ',' + h.y + ',' + h.z;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ x: h.x, y: h.y, z: h.z });
    }
    return out;
  }

  /** Release everything held by the player (death, dimension change). `at`: where the leads drop. */
  releasePlayerHeld(drop: boolean, at?: { x: number; y: number; z: number }, id?: string): void {
    for (const m of this.heldByPlayer(id)) {
      this.held.delete(m);
      m.leashHome = null;
      this.version++;
      if (drop) this.last?.dropLead(at?.x ?? m.x, (at?.y ?? m.y) + 0.5, at?.z ?? m.z);
    }
  }

  /** Forget every leash without drops (world unload / dimension swap clears the mobs anyway). */
  clear(): void {
    for (const m of this.held.keys()) m.leashHome = null;
    this.held.clear();
    this.version++;
  }

  /** Re-point mob holders after a reload: a holder id that no longer exists is dropped. */
  holderPos(h: LeashHolder, ctx: LeashCtx, out: [number, number, number]): boolean {
    switch (h.kind) {
      case 'player': {
        const q = h.id !== undefined ? ctx.playerById?.(h.id) : ctx.player;
        if (!q) return false;
        out[0] = q.x; out[1] = q.y + 1; out[2] = q.z;
        return q.alive;
      }
      case 'knot': out[0] = h.x + 0.5; out[1] = h.y + 0.6; out[2] = h.z + 0.5; return ctx.knotValid ? ctx.knotValid(h.x, h.y, h.z) : true;
      case 'mob': {
        const m = ctx.mobById(h.id);
        if (!m || !m.alive) return false;
        out[0] = m.x; out[1] = m.y + m.height * 0.5; out[2] = m.z;
        return true;
      }
    }
  }

  /** Once per game tick, after mobs ticked. */
  tick(ctx: LeashCtx): void {
    this.last = ctx;
    if (this.held.size === 0) return;
    const p: [number, number, number] = [0, 0, 0];
    for (const [mob, holder] of [...this.held]) {
      if (!this.held.has(mob)) continue;
      // a dying / despawned mob, a dead holder or a broken fence all release the lead
      if (!mob.alive || !this.holderPos(holder, ctx, p)) { this.detach(mob, true); continue; }
      const dx = p[0] - mob.x, dy = p[1] - (mob.y + mob.height * 0.5), dz = p[2] - mob.z;
      const d = Math.hypot(dx, dy, dz);
      if (d > LEASH_MAX) { this.detach(mob, true); continue; }
      // stroll only around the holder
      mob.leashHome = { x: p[0], z: p[2], r: LEASH_WANDER };
      if (d > LEASH_PULL) {
        const cx = dx / d, cy = dy / d, cz = dz / d;
        mob.vx += Math.sign(cx) * cx * cx * 0.4;
        mob.vy += Math.sign(cy) * cy * cy * 0.4;
        mob.vz += Math.sign(cz) * cz * cz * 0.4;
      }
    }
  }
}

/** The leads of the world being simulated. A server keeps one sim per dimension and swaps it in with `useLeashes`. */
export let leashes = new LeashSim();
export function useLeashes(sim: LeashSim): void { leashes = sim; }

// ------------------------------------------------------------ right-click helpers

export interface LeadUseEnv {
  creative: boolean;
  /** Which player is clicking (server); absent = the local player. */
  playerId?: string;
  /** Consume one lead from the hand (survival only; the helper checks `creative`). */
  consumeHeld(n: number): void;
  dropLead(x: number, y: number, z: number): void;
}

/**
 * Right-click a mob. A mob the player holds is released (the lead drops except
 * in creative); a lead in hand leashes a leashable, free mob. True = handled.
 * `leadId` is the item id of the lead (I.LEAD).
 */
export function leadUseOnMob(mob: Mob, heldId: number, leadId: number, env: LeadUseEnv, sim: LeashSim = leashes): boolean {
  const h = sim.holderOf(mob);
  if (h && h.kind === 'player' && h.id === env.playerId) {
    sim.detach(mob, false);
    if (!env.creative) env.dropLead(mob.x, mob.y + 0.5, mob.z);
    return true;
  }
  if (heldId === leadId && !h && canLeash(mob) && sim.attach(mob, env.playerId !== undefined ? { kind: 'player', id: env.playerId } : { kind: 'player' })) {
    if (!env.creative) env.consumeHeld(1);
    return true;
  }
  return false;
}

/**
 * Right-click a fence cell. Mobs the player holds are tied to a knot there;
 * with none held, an existing knot is untied (one lead per mob, none in creative).
 * Returns what happened.
 */
export function leadUseOnFence(x: number, y: number, z: number, env: LeadUseEnv, sim: LeashSim = leashes): 'tied' | 'untied' | 'none' {
  const mine = sim.heldByPlayer(env.playerId);
  if (mine.length) {
    sim.releasePlayerHeld(false, undefined, env.playerId);
    for (const m of mine) sim.attach(m, { kind: 'knot', x, y, z });
    return 'tied';
  }
  const tied = sim.heldByKnot(x, y, z);
  if (!tied.length) return 'none';
  for (const m of tied) {
    sim.detach(m, false);
    if (!env.creative) env.dropLead(m.x, m.y + 0.5, m.z);
  }
  return 'untied';
}

/** `knotValid` for a world: the cell is still a fence. */
export const fenceStands = (world: World) => (x: number, y: number, z: number): boolean => isFence(world.getBlock(x, y, z));
